/**
 * ai_agent — re-export shim over bunnyquery/engine.
 *
 * The chat request/transport/prompt logic now lives in the framework-agnostic
 * `bunnyquery/engine` package (shared with the BunnyQuery widget). This file
 * keeps the historical `@/code/ai_agent` import path working unchanged for the
 * rest of the app, wires the engine's transport to this app's skapi instance,
 * and keeps the two pieces of Vue-reactive, app-level shared state that are NOT
 * engine-owned (they are read/written across dbfile.vue, agent.vue, main.vue).
 */
import { reactive, ref } from 'vue';
import { skapi } from '@/main';
import { MCP_BASE_URL } from './mcp_oauth';
import { configureChatEngine, ChatSession, indexDoneUniqueId, runIndexUniqueId, type ChatHost, type BgTaskEntry, type IndexRunPatch } from 'bunnyquery/engine';

// Inject this app's transport + MCP endpoint into the engine. `poll` is omitted:
// the npm-bundled skapi-js used here resolves clientSecretRequest with the final
// body directly (the BunnyQuery widget, on skapi-js@latest, sets poll: 0).
configureChatEngine({
	clientSecretRequest: (opts: any) => (skapi.clientSecretRequest as any)(opts),
	clientSecretRequestHistory: (params: any, fetchOptions: any) =>
		(skapi as any).clientSecretRequestHistory(params, fetchOptions),
	mcpBaseUrl: MCP_BASE_URL,
	windowedIndexing: true,
	mintIndexDoneMarker: ({ service, storagePath }) => { void mintIndexDoneMarker(service, storagePath); },
	upsertIndexRunRecord: ({ service, storagePath, patch }) => { void upsertIndexRunRecord(service, storagePath, patch); },
	// Single-item csr-poll point lookup: how the engine hydrates a compact
	// history stub's real body when the user expands an indexing row.
	csrHistoryItemLookup: (fullId: string, service: string, owner: string) =>
		(skapi as any).util.request('csr-poll', { id: fullId, service, owner }, { auth: true }),
});

export * from 'bunnyquery/engine';

// Delete a file's AI-index record ("src::<storagePath>") ahead of a re-index or
// overwrite, so the agent re-creates it fresh instead of colliding/duplicating.
// The skapi backend cascades a src:: record delete to every record that
// references it (its reference-linked children), so this single call clears the
// file's whole reference-linked index subtree. Best-effort: a missing record (a
// file that was never indexed) or a permission/transport error must NOT fail the
// upload — the subsequent index request still runs and re-creates the record.
export async function deleteFileIndexRecord(service: string, storagePath: string): Promise<void> {
	if (!service || !storagePath) return;
	const prefix = 'src::' + storagePath;

	// Deleting only the exact "src::<path>" record leaves orphans behind. The agent also
	// creates DECORATED unique_ids of its own for per-window summaries, seen in real runs as
	// "src::<path>::sheet_기체::R54_window" and "src::<path>|file_summary|window190". Those are
	// separate records, NOT reference-linked children of the plain one, so the backend's cascade
	// never touches them and a re-index stacks a fresh set on top of the old. So: find everything
	// whose unique_id STARTS WITH the prefix (getUniqueId with '>=' is a starts-with search on a
	// string value) and delete all of it.
	let ids: string[] = [];
	try {
		const found: any = await (skapi as any).getUniqueId(
			{ unique_id: prefix, condition: '>=' },
			{ fetchMore: false, limit: 1000 }
		);
		ids = (found?.list || [])
			.map((row: any) => String(row?.unique_id || ''))
			.filter(Boolean);

		// A bare prefix match also catches a DIFFERENT FILE: "src::report.xlsx" is a prefix of
		// "src::report.xlsx.bak" and "src::report.xlsx (1).xlsx", both of which can be real files with
		// their own index, and there is no cheap existence probe to ask with.
		//
		// So a continuation only counts as a DECORATION when its first character is one that cannot
		// begin a filename: < > : " / \ | ? *  (the reserved set). The agent's own decorations use
		// them ("src::<path>::sheet_기체::R54_window", "src::<path>|file_summary|window190"), while the
		// real-world derived names that would be false positives continue with '.', a space, '-' or
		// '_', none of which are in the set. That asymmetry is the whole point of choosing it.
		//
		// Caveat: these characters are legal in an S3 key and nothing sanitises uploaded filenames, so
		// this is "very unlikely to collide", not "impossible". A false positive needs a file whose
		// path is ANOTHER file's complete path plus one of these characters plus more, which is
		// contrived. Anything not matching is left alone and logged rather than guessed at.
		const DECORATION_START = /^[<>:"/\\|?*]/;
		const decorations = ids.filter(id => id !== prefix && DECORATION_START.test(id.slice(prefix.length)));
		const ambiguous = ids.filter(id => id !== prefix && !decorations.includes(id));
		if (ambiguous.length) {
			// Visible rather than silent: these MIGHT be this file's leftovers, or another file
			// entirely. Left in place on purpose.
			console.warn('[ai_agent] deleteFileIndexRecord: leaving ' + ambiguous.length
				+ ' ambiguous prefix match(es) in place, cannot prove they belong to '
				+ storagePath + ':', ambiguous);
		}
		ids = decorations;
	} catch (err) {
		console.warn('[ai_agent] deleteFileIndexRecord: unique-id sweep failed (non-fatal)', storagePath, err);
	}

	if (!ids.includes(prefix)) ids.push(prefix);   // always try the plain one, even if unlisted

	// The backend's "indexing finished" marker (done::<path>) references the src::
	// record, so the cascade normally sweeps it - but records created before the
	// cascade flag existed have no cascade, and a re-index must NEVER start with a
	// stale "finished" verdict standing. Explicit delete, same best-effort rules.
	// The run:: status record rides the same cascade and needs the same explicit
	// delete for the same reason: a rerun must never inherit the old run's verdict.
	ids.push(indexDoneUniqueId(storagePath));
	ids.push(runIndexUniqueId(storagePath));

	// Best-effort and INDEPENDENT per id: a missing record (a file that was never indexed) or a
	// permission error must not stop the rest, nor fail the upload. The subsequent index request
	// still runs.
	//
	// Deleting the plain record ALSO cascades to every record referencing it, which is what clears
	// the bulk of the subtree whatever unique_id the agent gave those children. That cascade is not
	// automatic: it happens only because ensureFileIndexRecord sets
	// source.can_remove_referencing_records on the src:: record. Records written BEFORE that flag
	// was added have no cascade and are not reachable by the prefix sweep either, so a file indexed
	// before it still needs a one-off cleanup.
	for (const unique_id of ids) {
		try {
			await (skapi as any).deleteRecords({ service, unique_id });
		} catch (err) {
			console.warn('[ai_agent] deleteFileIndexRecord failed (non-fatal)', unique_id, err);
		}
	}
}

// Create the file's "src::<path>" record BEFORE any indexing pass runs, so that every pass has a
// reference target that is guaranteed to exist.
//
// Previously this record was created opportunistically by whichever indexing pass happened to do
// it, usually the first, while all 28 passes referenced it. Passes are separate model turns with no
// memory of each other (indexing turns are filtered out of chat history), so a pass could not know
// whether it existed, and delete-then-repost above deliberately destroys it at the start of a
// re-index. When it was absent the backend rejected every referencing row with
// NOT_EXISTS: 'Record of "reference.unique_id": "src::<path>" does not exist'. Measured in a real
// run: 20 records sent, 0 saved.
//
// Only the metadata the CLIENT already knows goes in here. Sheet names, column headers and row
// counts require extraction, so the agent enriches this record later; it must not be blocked on
// information nobody has yet.
// Client-side twin of the MCP server's /internal/index-complete: mint the
// durable "indexing finished" marker for a run whose completion THIS client
// knows deterministically (single-pass settle, or a client-driven chain's
// completion token — the engine's mintIndexDoneMarker hook decides when).
// Identical record shape so every reader treats them the same, including the
// reference to the src:: record, which is what makes a re-index's
// delete-then-repost cascade the marker away. Best-effort: the marker already
// existing (a redelivered settle) is completion, not a failure.
export async function mintIndexDoneMarker(service: string, storagePath: string): Promise<void> {
	if (!service || !storagePath) return;
	try {
		await (skapi as any).postRecord(null, {
			service,
			unique_id: indexDoneUniqueId(storagePath),
			table: { name: '__INDEXING__', access_group: 'authorized' },
			reference: 'src::' + storagePath,
			data: { source: storagePath, completed_at: Date.now() },
		});
	} catch (err: any) {
		const msg = String(err?.message || err || '');
		if (msg.indexOf('is already taken') === -1) {
			console.warn('[ai_agent] mintIndexDoneMarker failed (non-fatal)', storagePath, msg);
		}
	}
}

// Create-or-update the per-file indexing RUN record ("run::<path>"). The engine
// fires this at first-pass enqueue (status 'working') and at every ending it
// observes (done/error/cancelled); the worker writes the same record through the
// MCP internal routes for chain endings only it can see. The records API has no
// upsert, so the order is chosen by which case is COMMON per status: a 'working'
// mint usually targets a MISSING record (the reindex cascade swept the old one)
// and creates straight away; a terminal flip usually targets an EXISTING one and
// looks it up first — create-first there guaranteed a visible 400 ("is already
// taken") in the console on every flip and every stale-sweep re-observation,
// which read as a bug. Precedence: a 'working' write never overwrites a terminal
// status unless it is a NEW run (started after the old run finished); a terminal
// re-write of the same status is skipped outright.
export async function upsertIndexRunRecord(
	service: string,
	storagePath: string,
	patch: IndexRunPatch,
): Promise<void> {
	if (!service || !storagePath || !patch || !patch.status) return;
	const uid = runIndexUniqueId(storagePath);
	const TERMINAL: Record<string, boolean> = { done: true, error: true, cancelled: true };
	const patchData = (base: any) => {
		const d: any = Object.assign({}, base || {}, { source: storagePath, status: patch.status });
		if (patch.filename) d.filename = patch.filename;
		if (typeof patch.started === 'number') d.started = patch.started;
		if (typeof patch.finished === 'number') d.finished = patch.finished;
		if (patch.error) d.error = patch.error;
		if (patch.queue) d.queue = patch.queue;
		// The chat that owns the run: a record is keyed by storage path alone,
		// but a chat is per (project, platform) — see IndexRunPatch.platform.
		if (patch.platform) d.platform = patch.platform;
		return d;
	};
	// Absent and unfetchable both read as "no record" (fetching a missing
	// unique_id can throw rather than return an empty list).
	const lookup = async (): Promise<any | null> => {
		try {
			const found: any = await (skapi as any).getRecords({ service, unique_id: uid });
			return found?.list?.[0] || null;
		} catch { return null; }
	};
	// Returns true when the record is settled (updated, or correctly left alone).
	const updateExisting = async (rec: any): Promise<void> => {
		const existing = rec.data || {};
		if (patch.status === 'working' && TERMINAL[String(existing.status)]) {
			// A NEW run may reopen a terminal record: an orphaned verdict from a
			// path's previous life must not freeze the fresh run out. Only a LATE
			// create from the run the record already describes must lose, and its
			// `started` predates the settle that closed it.
			const endedAt = typeof existing.finished === 'number' ? existing.finished
				: typeof existing.started === 'number' ? existing.started : 0;
			if (!(typeof patch.started === 'number' && patch.started > endedAt)) return;
		}
		// Same terminal verdict already stored: nothing to say. Re-writing it on
		// every stale-sweep re-observation was pure write noise.
		if (patch.status !== 'working' && String(existing.status) === patch.status) return;
		await (skapi as any).postRecord(null, {
			service,
			record_id: rec.record_id,
			data: patchData(existing),
		});
	};
	const create = (withRef: boolean) => (skapi as any).postRecord(null, {
		service,
		unique_id: uid,
		table: { name: '__INDEXING__', access_group: 'authorized' },
		// Cascade linkage, same as done::: a re-index's delete of src:: sweeps
		// the old run record away, so a rerun of the same path starts clean.
		...(withRef ? { reference: 'src::' + storagePath } : {}),
		data: patchData(null),
	});
	try {
		if (patch.status !== 'working') {
			const rec = await lookup();
			if (rec?.record_id) { await updateExisting(rec); return; }
		}
		try {
			await create(true);
			return;
		} catch (err: any) {
			const msg = String(err?.message || err || '');
			if (msg.indexOf('is already taken') === -1) {
				// Not the exists-signal — almost always the src:: record not
				// existing yet. Mint it and retry WITH the reference (a
				// reference-less run record cannot be cascade-swept on reindex);
				// only if that still fails does a reference-less create beat
				// having no record at all.
				try {
					await ensureFileIndexRecord(service, storagePath);
					await create(true);
					return;
				} catch (errRef: any) {
					const msgRef = String(errRef?.message || errRef || '');
					if (msgRef.indexOf('is already taken') === -1) {
						try {
							await create(false);
							return;
						} catch (err2: any) {
							const msg2 = String(err2?.message || err2 || '');
							if (msg2.indexOf('is already taken') === -1) {
								console.warn('[ai_agent] upsertIndexRunRecord create failed (non-fatal)', storagePath, msg2);
								return;
							}
						}
					}
				}
			}
		}
		// Taken after all (created behind our back, or a 'working' mint over a
		// surviving record): settle it as an update.
		const rec2 = await lookup();
		if (rec2?.record_id) await updateExisting(rec2);
	} catch (err: any) {
		console.warn('[ai_agent] upsertIndexRunRecord failed (non-fatal)', storagePath, String(err?.message || err || ''));
	}
}

export async function ensureFileIndexRecord(
	service: string,
	storagePath: string,
	meta?: { name?: string; mime?: string; size?: number },
): Promise<void> {
	if (!service || !storagePath) return;
	try {
		await (skapi as any).postRecord(null, {
			service,
			unique_id: 'src::' + storagePath,
			table: { name: 'file_summaries', access_group: 'authorized' },
			// Deleting this record must take every record extracted from the file with it.
			// Without the flag the backend's cascade is skipped outright: del_records only
			// follows references when the REFERENCED record carries
			// prv_acs.can_remove_referencing_records, which is what `source` populates. So
			// deleting the file left its rows and photos behind. deleteFileIndexRecord's
			// unique_id sweep does not cover them either, because it matches ids starting
			// with "src::<path>" while the agent names row/photo records things like
			// "<file>::<sheet>::PHOTO-C28-1", which share no prefix with it. The cascade is
			// the only mechanism that catches a child whatever unique_id it was given.
			source: { can_remove_referencing_records: true },
			data: {
				file_name: meta?.name || storagePath.split('/').pop() || storagePath,
				storage_path: storagePath,
				mime_type: meta?.mime || null,
				size_bytes: typeof meta?.size === 'number' ? meta.size : null,
				indexed_at: Date.now(),
				note: 'File-level record created at upload. The indexing agent enriches this with sheet names, column headers and row counts.',
			},
		});
	} catch (err) {
		// Non-fatal by design: if this fails, indexing still runs and the agent may create the
		// record itself as it did before. Losing the guarantee is better than losing the upload.
		console.warn('[ai_agent] ensureFileIndexRecord failed (non-fatal)', storagePath, err);
	}
}

// App-level shared state — NOT engine-owned. Populated by dbfile.vue immediately
// when notifyAgentSaveAttachment returns, drained by agent.vue to inject
// "Thinking…" bubbles, and watched by main.vue's fallback poller. Must stay
// Vue-reactive here so those components react to changes.
export const bgTaskQueue: BgTaskEntry[] = reactive([]);

// True while the agent chat view (agent.vue) is mounted. While it's open it owns
// polling/rendering of bgTaskQueue tasks; while closed, main.vue's fallback
// poller drives task completion so records still auto-refresh.
export const agentViewMounted = ref(false);

/* ---- shared ChatSession singleton (Stage E) -------------------------------
 * The chat state machine lives in bunnyquery/engine. agent.vue mounts/unmounts
 * on every service/tab switch, so the session is a module-level SINGLETON (its
 * lifetime = the app session): a per-component session would drop bgTaskQueue /
 * pendingAgentRequests / history-cache state on unmount and break the
 * dbfile.vue → queue → main.vue fallback-poller coordination.
 *
 * The host (identity, DOM, scroll, Service-class I/O) is owned by the VIEW, which
 * installs a concrete ChatHost on mount via setChatHost() and clears it on
 * unmount. The proxy throws only if an engine hook fires with no view mounted
 * (a late poll after unmount — the view guards those paths). */
let _liveHost: ChatHost | null = null;
const hostProxy = new Proxy({} as ChatHost, {
	get(_t, key) {
		if (!_liveHost) throw new Error('[ai_agent] chat host is not mounted');
		return (_liveHost as any)[key];
	},
});
export const chatSession = new ChatSession(hostProxy);
export function setChatHost(h: ChatHost | null) { _liveHost = h; }

// Make the engine's plain state POJO Vue-reactive so the view's
// computed(() => chatSession.state.<scalar>) track the engine's in-place writes.
// messages/attachments are additionally driven by shallowRef+triggerRef in the
// view (the engine mutates those arrays in place).
chatSession.state = reactive(chatSession.state);
// Share the bg-indexing queue (also read/written by dbfile.vue + main.vue) with
// the engine instance. aiChatHistoryCache + pendingAgentRequests are re-pointed
// at the project-list shared stores from within agent.vue (which imports them),
// avoiding an ai_agent ↔ project-list circular import here.
chatSession.bgTaskQueue = bgTaskQueue;
